import ReactDOM from 'react-dom/client';
import { EventType, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import { msalInstance, loginRequest, apiTokenRequest, silentRedirectUri } from '@/config/msalConfig';
import { setTokenAcquirer } from '@/services/apiClient';
import { initCookieConsent } from '@/utils/thirdPartyScripts';
import { App } from '@/app/App';
import '@/styles/globals.css';

// Async IIFE — all module-level work is non-blocking.
// createRoot + render(spinner) run synchronously before the first await,
// so FCP fires on the spinner before MSAL initialises.
// eslint-disable-next-line unicorn/prefer-top-level-await
void (async () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <div className="flex h-screen items-center justify-center bg-white">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-green-600" />
    </div>,
  );

  await msalInstance.initialize();

  // Handle redirect response (returns null if not coming back from a redirect)
  const redirectResponse = await msalInstance.handleRedirectPromise();

  // If inside a hidden iframe (MSAL silent renewal), stop — don't render the app
  if (window === window.parent) {
    // Set active account from redirect response or existing accounts
    if (redirectResponse?.account) {
      msalInstance.setActiveAccount(redirectResponse.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]!);
      }
    }

    // If no account is signed in, trigger redirect login immediately
    if (!msalInstance.getActiveAccount()) {
      await msalInstance.loginRedirect(loginRequest);
    } else {
      initCookieConsent();

      // Wire MSAL token acquisition into the fetch-based API client
      setTokenAcquirer(async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error('No active MSAL account');
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...apiTokenRequest,
            account,
            redirectUri: silentRedirectUri,
          });
          return response.accessToken;
        } catch (err) {
          if (
            err instanceof InteractionRequiredAuthError ||
            (err instanceof BrowserAuthError &&
              (err.errorCode === 'block_iframe_reload' ||
                err.errorCode === 'monitor_window_timeout'))
          ) {
            try {
              const response = await msalInstance.acquireTokenPopup({
                ...apiTokenRequest,
                account,
                redirectUri: silentRedirectUri,
              });
              return response.accessToken;
            } catch (popupErr) {
              console.error('[MSAL] acquireTokenPopup failed, falling back to redirect', popupErr instanceof Error ? popupErr.message : String(popupErr));
              await msalInstance.acquireTokenRedirect({
                ...apiTokenRequest,
                account,
              });
              throw new Error('Redirecting for token acquisition');
            }
          }
          throw err;
        }
      });

      // Listen for sign-in events to keep active account in sync
      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const result = event.payload as AuthenticationResult;
          if (result.account) {
            msalInstance.setActiveAccount(result.account);
          }
        }
      });

      // Replace the loading shell with the full app (reuses the same root)
      root.render(<App />);
    }
  }
})();
